"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

const LINE_CATEGORY_OPTIONS = [
  { value: 'TRUNK', label: 'Trunk' },
  { value: 'MAIN', label: 'Main' },
  { value: 'SECONDARY', label: 'Secondary' },
  { value: 'LOCAL', label: 'Local' },
]

const PROVIDER_TYPE_OPTIONS = [
  { value: 'PLK', label: 'PLK (National)' },
  { value: 'PRIVATE', label: 'Private' },
]

export default function CreateTariffRatePage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'name',
      label: t('tariff_rates.fields.name', 'Name'),
      type: 'text',
      required: true,
      placeholder: t('tariff_rates.fields.namePlaceholder', 'e.g. PLK TRUNK 2026'),
    },
    {
      id: 'lineCategory',
      label: t('tariff_rates.fields.lineCategory', 'Line Category'),
      type: 'select',
      required: true,
      options: LINE_CATEGORY_OPTIONS,
    },
    {
      id: 'ratePerTonKm',
      label: t('tariff_rates.fields.ratePerTonKm', 'Rate per ton-km'),
      type: 'number',
      required: true,
      placeholder: '0.0245',
    },
    {
      id: 'currency',
      label: t('tariff_rates.fields.currency', 'Currency'),
      type: 'text',
      defaultValue: 'PLN',
      placeholder: 'PLN',
    },
    {
      id: 'validFrom',
      label: t('tariff_rates.fields.validFrom', 'Valid From'),
      type: 'date',
      required: true,
    },
    {
      id: 'validTo',
      label: t('tariff_rates.fields.validTo', 'Valid To'),
      type: 'date',
    },
    {
      id: 'providerType',
      label: t('tariff_rates.fields.providerType', 'Provider Type'),
      type: 'select',
      required: true,
      options: PROVIDER_TYPE_OPTIONS,
    },
    {
      id: 'infrastructureManager',
      label: t('tariff_rates.fields.infrastructureManager', 'Infrastructure Manager'),
      type: 'text',
      placeholder: t('tariff_rates.fields.infrastructureManagerPlaceholder', 'For private providers only'),
    },
    {
      id: 'minCharge',
      label: t('tariff_rates.fields.minCharge', 'Minimum Charge'),
      type: 'number',
      placeholder: '0.00',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'tariff',
      title: t('tariff_rates.groups.tariff', 'Tariff Details'),
      fields: ['name', 'lineCategory', 'providerType', 'infrastructureManager'],
    },
    {
      id: 'pricing',
      title: t('tariff_rates.groups.pricing', 'Pricing'),
      fields: ['ratePerTonKm', 'currency', 'minCharge'],
    },
    {
      id: 'validity',
      title: t('tariff_rates.groups.validity', 'Validity Period'),
      fields: ['validFrom', 'validTo'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('tariff_rates.create.title', 'New Tariff Rate')}
          backHref="/backend/route_planning"
          fields={fields}
          groups={groups}
          submitLabel={t('tariff_rates.create.submit', 'Create Tariff Rate')}
          cancelHref="/backend/route_planning"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              organizationId,
              tenantId,
            }
            const { result: created } = await createCrud<{ id?: string }>('tariff_rates/tariff-rates', payload)
            flash(t('tariff_rates.flash.created', 'Tariff rate created'), 'success')
            router.push(`/backend/tariff-rates/${created?.id}`)
          }}
        />
      </PageBody>
    </Page>
  )
}
